import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { useQueryState, parseAsString } from '@lib/queryState';
import { Auth } from 'aws-amplify';
import { Modal } from 'antd';
import useAuth from '@vl-core/useAuth';

export default function useAutoSSO(amplifyConfigured: boolean, onAutoSSO = () => {}) {
  const { IDENTITY_PROVIDERS } = useRuntimeConfig();
  const providers = IDENTITY_PROVIDERS?.providers;
  const autoSSO = useQueryState('autosso', parseAsString);
  const autoSSOed = useMemo(() => sessionStorage.getItem('autosso') === '1', []);

  useEffect(() => {
    if (autoSSO?.[0] && providers && amplifyConfigured) {
      const autoSsoProvider = providers?.find((p) => p?.provider === autoSSO[0]);
      if (autoSsoProvider) {
        // only if not already signed in
        Auth.currentSession()
          .then(() => console.log('useAutoSSO: Already signed in, resisting the temptation to auto-sign in.'))
          .catch(() => {
            sessionStorage.setItem('autosso', '1');
            console.log('Auth.federatedSignIn: useAutoSSO');
            // @ts-ignore
            Auth.federatedSignIn({ provider: autoSsoProvider.provider });
          });
      }
    }
  }, [autoSSO, providers, amplifyConfigured]);

  if (autoSSOed && onAutoSSO && sessionStorage.getItem('autosso')) {
    sessionStorage.removeItem('autosso');

    onAutoSSO();
  }

  return autoSSO;
}

export function useAutoSSOWithTransferModal(content: ReactNode, amplifyConfigured = true) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const service_name = user?.service_name || '';
  const modal = useCallback(
    () =>
      Modal.warning({
        width: 670,
        title: (
          <div>
            <img height={service_name ? 35 : 55} src="/static/images/hbsuk_logo.png" alt="" />
            &nbsp;&nbsp;You have now been transferred <span style={{ fontWeight: 'bold' }}>
              {t('from AXA Health')}
            </span>{' '}
            {t('to HBSUK')}
            <hr style={{ marginBlock: 15 }} />
          </div>
        ),
        modalRender: (originalNode) => <div data-testid="you-have-been-trasferred-modal">{originalNode}</div>,
        content,
        icon: null
      }),
    [content, service_name]
  );

  useAutoSSO(amplifyConfigured, modal);
}

export function TransferMessageBody({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <>
      <p>{t('Thank you for choosing to use HBSUK’s online outpatient services.')}</p>
      <p>{t('During your healthcare journey, HBSUK will be there to support you every step of')}</p>
      {children}
      <p>
        <span style={{ fontWeight: 'bold' }}>{t('Please note that')}</span>{' '}
        {t('your use of this service is subject to')}{' '}
        <a style={{ fontWeight: 'bold', textDecoration: 'underline' }} href="/patients/legal">
          {t('HBSUK’s terms and conditions.')}
        </a>
      </p>
    </>
  );
}
